import React from "react";
import { useNavigate } from "react-router-dom";
import * as Styled from "./styles";
import { useClinicalBudget } from "shared/providers/ClinicalBudget";
import { useAuth } from "shared/providers/auth";
import { saveOrcamento, updateOrcamento } from "requests/ClinicalBudget";
import { sendAppraisal } from "requests/Appraisal";
import { sendSchedule } from "requests/SendSchedule";
import toast from "react-hot-toast";
import { IoArrowBack, IoCheckmark, IoClose, IoReceiptOutline, IoList } from "react-icons/io5";

const ClinicalBudgetConfirm: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const {
    setStep,
    editMode,
    editOrcamentoId,
    patientObj,
    budgetItems,
    totalItens,
    subtotal,
    total,
    desconto,
    entrada,
    parcelas,
    dataVencimento,
    obsDesconto,
    fatorDesconto,
    formatMoeda,
    parseMoeda,
    saving,
    setSaving,
    showValues,
    getElementoLabel,
  } = useClinicalBudget();

  const handleBack = () => {
    setStep(showValues ? 2 : 1);
  };

  const handleCancel = () => {
    if (window.confirm('Deseja cancelar este orçamento? Todas as alterações serão perdidas.')) {
      navigate(-1);
    }
  };

  const handleSave = async () => {
    if (budgetItems.length === 0) {
      alert('Adicione pelo menos um procedimento ao orçamento!');
      return;
    }

    if (showValues) {
      const descontoParsed = parseMoeda(desconto);
      if (descontoParsed > 0 && obsDesconto.trim() === '') {
        alert('Informe o motivo do desconto.');
        setStep(2);
        return;
      }

      if (parcelas < 1) {
        alert('Informe o número de parcelas.');
        setStep(2);
        return;
      }

      if (!dataVencimento || dataVencimento.length < 10) {
        alert('Informe a data do primeiro vencimento.');
        setStep(2);
        return;
      }

      const dateParts = dataVencimento.split('/');
      if (dateParts.length === 3) {
        const dataVcto = new Date(
          parseInt(dateParts[2]),
          parseInt(dateParts[1]) - 1,
          parseInt(dateParts[0]),
          23, 59
        );
        const hoje = new Date();
        
        if (dataVcto < hoje) {
          alert('A data do primeiro vencimento precisa ser maior ou igual à data atual.');
          setStep(2);
          return;
        }

        const diff = (dataVcto.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24);
        if (diff > 90) {
          alert('A data do primeiro vencimento não pode ser maior que 90 dias.');
          setStep(2);
          return;
        }
      }
    }

    setSaving(true);

    try {
      const userId = user?.data?.id || "";
      const patientId = patientObj?.pac_id;

      const payload = {
        pac_id: patientId,
        ...(patientObj?.id ? { agenda_id: patientObj.id } : {}),
        ...(editMode && editOrcamentoId ? { idOrcamento: editOrcamentoId } : {}),
        itens: budgetItems.map(item => ({
          procId: item.procId,
          qtde: item.qtde,
          elemento: item.elemento,
          valor: item.valor,
          grupoId: item.grupoId || 0,
          faces: item.faces || '',
          catprocId: item.catprocId || 0,
          gpitemId: item.gpitemId || 0,
          obsPersonalizado: item.obsPersonalizado || '',
        })),
        desconto: desconto,
        entrada: entrada,
        parcelas: parcelas,
        dataVencimento: dataVencimento,
        obs: obsDesconto,
        fatorDesconto: fatorDesconto,
      };

      const response = editMode
        ? await updateOrcamento(userId, payload)
        : await saveOrcamento(userId, payload);

      if (response.data?.success) {
        const createdBudgetId = response.data.data?.idOrcamento || 0;
        let shouldNavigateHome = true;

        toast.success(
          editMode
            ? `Orçamento atualizado com sucesso! Nº ${createdBudgetId}`
            : `Orçamento criado com sucesso! Nº ${createdBudgetId}`
        );
        
        if (!editMode) {
          const pendingData = localStorage.getItem('pendingAvaliation');
          if (pendingData) {
            try {
              const avaliacaoData = JSON.parse(pendingData);
              
              if (avaliacaoData.type === 'appraisal') {
                const { status, data } = await sendAppraisal(avaliacaoData.payload);
                if ([200, 201].includes(status)) {
                  toast.success(data.message || "Avaliação finalizada com sucesso!");
                } else {
                  shouldNavigateHome = false;
                  toast.error(data?.message || "Erro ao finalizar avaliação.");
                }
              } else if (avaliacaoData.type === 'schedule') {
                const payloadWithBudget = {
                  ...avaliacaoData.payload,
                  idBudget: createdBudgetId || null,
                  schedules: Array.isArray(avaliacaoData.payload?.schedules)
                    ? avaliacaoData.payload.schedules.map((schedule: any) => ({
                        ...schedule,
                        idBudget:
                          schedule?.idBudget && Number(schedule.idBudget) > 0
                            ? schedule.idBudget
                            : createdBudgetId || null,
                      }))
                    : [],
                };

                const { status, data } = await sendSchedule(payloadWithBudget);
                if ([200, 201].includes(status)) {
                  toast.success(data.message || "Avaliação finalizada com sucesso!");
                } else {
                  shouldNavigateHome = false;
                  toast.error(data?.message || "Erro ao finalizar avaliação.");
                }
              }
              
              if (shouldNavigateHome) {
                localStorage.removeItem('pendingAvaliation');
              }
            } catch (err) {
              shouldNavigateHome = false;
              console.error('Erro ao finalizar avaliação:', err);
              toast.error("Orçamento salvo, mas houve erro ao finalizar avaliação.");
            }
          }
          
          if (shouldNavigateHome) {
            localStorage.removeItem("avaliationFormData");
            localStorage.removeItem("clinicalEvaluationFormData");
          }
        }

        if (shouldNavigateHome) {
          localStorage.removeItem("personAttendance");
          navigate("/home");
        }
      } else {
        toast.error(response.data?.message || 'Erro ao salvar orçamento');
      }
    } catch (error) {
      console.error('Erro ao salvar orçamento:', error);
      toast.error('Erro ao salvar orçamento. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const descontoParsed = parseMoeda(desconto);
  const entradaParsed = parseMoeda(entrada);

  const formatElem = (elem: number) => getElementoLabel(elem);

  return (
    <Styled.Container>
      <Styled.SummaryCard>
        <Styled.SummaryHeader>
          <IoReceiptOutline size={20} color="white" />
          <h4>{editMode ? 'Resumo da Edição' : 'Resumo Final do Orçamento'}</h4>
        </Styled.SummaryHeader>
        <Styled.SummaryBody>
          <Styled.SummaryRow>
            <Styled.SummaryLabel>Itens</Styled.SummaryLabel>
            <Styled.SummaryValue showValue>{totalItens} procedimento(s)</Styled.SummaryValue>
          </Styled.SummaryRow>
          
          {showValues && (
            <>
              <Styled.SummaryRow>
                <Styled.SummaryLabel>Sub-total</Styled.SummaryLabel>
                <Styled.SummaryValue showValue={showValues}>
                  R$ {formatMoeda(subtotal)}
                </Styled.SummaryValue>
              </Styled.SummaryRow>
              
              {descontoParsed > 0 && (
                <Styled.SummaryRow>
                  <Styled.SummaryLabel>Desconto</Styled.SummaryLabel>
                  <Styled.SummaryValue showValue={showValues} style={{ color: '#ef4444' }}>
                    - R$ {formatMoeda(descontoParsed)}
                  </Styled.SummaryValue>
                </Styled.SummaryRow>
              )}
            </>
          )}
          
          {showValues && (
            <>
              <Styled.SummaryRow>
                <Styled.SummaryLabel>Parcelas</Styled.SummaryLabel>
                <Styled.SummaryValue showValue>{parcelas}x</Styled.SummaryValue>
              </Styled.SummaryRow>
              
              {entradaParsed > 0 && (
                <Styled.SummaryRow>
                  <Styled.SummaryLabel>Entrada</Styled.SummaryLabel>
                  <Styled.SummaryValue showValue={showValues}>
                    R$ {formatMoeda(entradaParsed)}
                  </Styled.SummaryValue>
                </Styled.SummaryRow>
              )}
              
              <Styled.SummaryRow>
                <Styled.SummaryLabel>1º Vencimento</Styled.SummaryLabel>
                <Styled.SummaryValue showValue>{dataVencimento}</Styled.SummaryValue>
              </Styled.SummaryRow>
            </>
          )}
          
          {showValues && (
            <Styled.SummaryRow highlight>
              <Styled.SummaryLabel bold>TOTAL</Styled.SummaryLabel>
              <Styled.SummaryValue highlight showValue={showValues}>
                R$ {formatMoeda(total)}
              </Styled.SummaryValue>
            </Styled.SummaryRow>
          )}
        </Styled.SummaryBody>
      </Styled.SummaryCard>

      <Styled.ItemsPreview>
        <Styled.ItemsHeader>
          <h4><IoList size={18} style={{ marginRight: 8 }} /> Itens do Orçamento</h4>
          <span>{totalItens} item(s)</span>
        </Styled.ItemsHeader>
        <Styled.ItemsList>
          {budgetItems.map(item => (
            <Styled.ItemRow key={item.id}>
              <Styled.ItemInfo>
                <div className="nome">{item.procNome}</div>
                <div className="meta">
                  Elem: {formatElem(item.elemento)}
                  {item.grupoNome && ` • ${item.grupoNome}`}
                  {item.faces && ` • Faces: ${item.faces}`}
                  {item.obsPersonalizado && ` • 📝 ${item.obsPersonalizado.substring(0, 40)}${item.obsPersonalizado.length > 40 ? '...' : ''}`}
                </div>
              </Styled.ItemInfo>
              {showValues && (
                <Styled.ItemValor showValue={showValues}>
                  R$ {formatMoeda(item.valor * item.qtde)}
                </Styled.ItemValor>
              )}
            </Styled.ItemRow>
          ))}
        </Styled.ItemsList>
      </Styled.ItemsPreview>

      {showValues && obsDesconto && (
        <Styled.SummaryCard>
          <Styled.SummaryBody>
            <Styled.SummaryLabel>Motivo do Desconto:</Styled.SummaryLabel>
            <p style={{ margin: '8px 0 0 0', color: '#475569', fontSize: '14px' }}>
              {obsDesconto}
            </p>
          </Styled.SummaryBody>
        </Styled.SummaryCard>
      )}

      <Styled.BtnFixed>
        <Styled.Button variant="secondary" onClick={handleBack}>
          <IoArrowBack size={20} />
          Voltar
        </Styled.Button>
        <Styled.Button variant="danger" onClick={handleCancel}>
          <IoClose size={20} />
          Cancelar
        </Styled.Button>
        <Styled.Button variant="primary" onClick={handleSave} disabled={saving}>
          <IoCheckmark size={20} />
          {editMode ? 'Atualizar' : 'Salvar'}
        </Styled.Button>
      </Styled.BtnFixed>

      {saving && (
        <Styled.LoadingOverlay>
          <div className="spinner" />
          <p>Salvando orçamento...</p>
        </Styled.LoadingOverlay>
      )}
    </Styled.Container>
  );
};

export { ClinicalBudgetConfirm };
